import { useCallback, useState } from "react";
import { getBulkCampaignStatistics } from "V2.0/services/bulkCampaign";

export type TBulkCampaignStatistics = {
  bulkCampaignId: string;
  // Polymorphic: call campaigns return callsAttempted / callsBlocked / callsFailed /
  // callsCompleted / callsNotAnswered / voicemail. Everything else (sms, whatsapp, email)
  // returns messagesAttempted / messagesSent / messagesFailed / messagesBlocked /
  // usersResponded. The response carries no campaignType, so the caller has to know which.
  campaignStatistics: Record<string, number>;
  // Already a percentage (0–100), not a fraction — a real response carries 60 for a
  // 27-of-45 campaign. Answered rate for calls, reply rate for chat.
  connectivityRate: number;
  totalMinutes: number;
  totalContacts: number;
  extractionBreakdown: Record<string, Record<string, number>> | null;
  responseByAttempt: Record<string, number> | null;
  // Follow-up calls still queued, counted one per contact with a pending slot. Always live —
  // served alongside a frozen report rather than from it — so it shrinks as follow-ups fire.
  // Optional for two reasons a caller must both tell apart from a genuine 0: an older backend
  // omits it entirely, and the current one drops it when the count query fails rather than
  // failing the whole report.
  followUpsScheduled?: number;
  status: string;
};

export default function useBulkCampaignStatistics(
  workspaceId: string,
  bulkCampaignId: string,
) {
  const [statistics, setStatistics] = useState<{
    data: TBulkCampaignStatistics | null;
    isLoading: boolean;
    error: unknown;
  }>({
    data: null,
    isLoading: true,
    error: null,
  });

  // Memoised so callers can list it in an effect's dependencies without re-fetching on every
  // render. It only ever changes when the campaign being looked at does.
  const fetchBulkCampaignStatistics = useCallback(async () => {
    // Re-entering the loading state matters on retry: without it a second failure would
    // render instantly with the previous error still on screen and no sign anything ran.
    setStatistics({ data: null, isLoading: true, error: null });

    try {
      const response = await getBulkCampaignStatistics({
        workspaceId,
        bulkCampaignId,
      });

      if (response.status === 200) {
        setStatistics({ data: response.data, isLoading: false, error: null });
        return;
      }

      setStatistics({ data: null, isLoading: false, error: response });
    } catch (e) {
      // httpService only toasts network/5xx. A 404 (campaign gone) or 400 (bad id) lands
      // here silently, so the modal has to surface it itself.
      setStatistics({ data: null, isLoading: false, error: e });
    }
  }, [workspaceId, bulkCampaignId]);

  return {
    statistics: statistics.data,
    isStatisticsLoading: statistics.isLoading,
    statisticsError: statistics.error,
    fetchBulkCampaignStatistics,
  };
}
